/**
 * Customer Resource Client
 * Handles customer-related API operations
 */

import { ApiClient } from './apiClient';
import type {
    Subscription as CoreSubscription,
    DeviceInfo,
    PromotionCode,
    OrderAddress,
    OrderSummary,
    PromotionSummary,
    PaymentSummary,
    OrderAdjustmentSummary,
} from '../types';

export interface CustomerAddress {
    firstName: string | null;
    lastName: string | null;
    address1: string | null;
    address2: string | null;
    city: string | null;
    province: string | null;
    country: string | null;
    zip: string | null;
    phone: string | null;
    email?: string | null;
}

export interface CustomerOrderSummary {
    id: string;
    totalPrice: number;
    currency: string;
    createdAt: string;
    status: string;
}

export interface CustomerInfos {
    customer: {
        id: string;
        email: string | null;
        firstName: string | null;
        lastName: string | null;
        externalCustomerId: string | null;
        lastOrderId: string | null;
        accountId: string;
        storeId: string;
        billingAddress: CustomerAddress | null;
        shippingAddress: Omit<CustomerAddress, 'email'> | null;
        currency: string | null;
        locale: string | null;
        draft: boolean;
        acceptsMarketing: boolean;
        createdAt: string;
        updatedAt: string;
        metadata: Record<string, unknown>;
        device: DeviceInfo | null;
        orders: CustomerOrderSummary[];
        subscriptions: CoreSubscription[];
    };
    promotionCodes: PromotionCode[];
}

export interface OrderWithRelations {
    id: string;
    currency: string;
    paidAmount: number;
    status: string;
    createdAt: string;
    metadata?: Record<string, unknown>;
    items: Record<string, unknown>[];
    summaries?: OrderSummary[];
    shippingAddress?: OrderAddress | null;
    billingAddress?: OrderAddress | null;
    pickupAddress?: Record<string, unknown> | null;
    checkoutSession?: {
        returnUrl?: string;
        [key: string]: unknown;
    };
    relatedOrders?: OrderWithRelations[];
    customer?: Record<string, unknown>;
    store?: Record<string, unknown>;
    account?: { id: string; name?: string };
    payments?: PaymentSummary[];
    promotions?: PromotionSummary[];
    subscriptions?: CoreSubscription[];
    adjustments: OrderAdjustmentSummary[];
}

export interface Subscription {
    id: string;
    status: string;
    createdAt: string;
    currency: string;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string | null;
    currentPeriodStart: string | null;
    quantity: number;
    trialEnd: string | null;
    customerId: string;
    customerEmail: string;
    customerName: string;
    priceCurrencyOptions: Record<string, { rate: number; amount: number; lock: boolean; date: string }>;
    priceInterval: string;
    priceIntervalCount: number;
    priceRecurring: boolean;
    productId: string;
    priceId: string;
    productTitle: string;
}

export interface SubscriptionsResponse {
    items: Subscription[];
    pagination: {
        page: number;
        pageSize: number;
        hasNext: boolean;
        nextPage: number | null;
        previousPage: number | null;
        totalItems: number;
    };
}

export class CustomerResource {
    constructor(private apiClient: ApiClient) { }

    /**
     * Get customer information by ID
     */
    async getCustomerInfos(customerId: string, storeId: string): Promise<CustomerInfos> {
        return this.apiClient.get(`/api/v1/customers/${customerId}`, {
            params: { storeId },
        });
    }

    /**
     * Update customer information
     */
    async updateCustomer(
        customerId: string,
        data: {
            email?: string;
            firstName?: string;
            lastName?: string;
            acceptsMarketing?: boolean;
            metadata?: Record<string, unknown>;
        }
    ): Promise<CustomerInfos> {
        return this.apiClient.patch(`/api/v1/customers/${customerId}`, data);
    }

    /**
     * Get customer orders
     */
    async getCustomerOrders(customerId: string, storeId: string): Promise<{ orders: OrderWithRelations[] }> {
        return this.apiClient.get(`/api/v1/orders/customer/${customerId}`, {
            params: { storeId },
        });
    }

    /**
     * Get customer subscriptions
     */
    async getCustomerSubscriptions(): Promise<SubscriptionsResponse> {
        return this.apiClient.get('/api/v1/subscriptions');
    }

    /**
     * Resume a subscription
     */
    async resumeSubscription(subscriptionId: string): Promise<void> {
        return this.apiClient.post('/api/v1/subscriptions/resume', {
            subscriptionId,
        });
    }

    /**
     * Cancel a subscription
     */
    async cancelSubscription(subscriptionId: string): Promise<void> {
        return this.apiClient.post('/api/v1/subscriptions/cancel', {
            subscriptionId,
        });
    }
}

